// pages/Agents/AgentSpace/AgentSpaceDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Button,
    Skeleton,
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import EmailIcon from '@mui/icons-material/Email';
import PollIcon from '@mui/icons-material/Poll';
import PeopleIcon from '@mui/icons-material/People';
import EventIcon from '@mui/icons-material/Event';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChatIcon from '@mui/icons-material/Chat';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import BoltIcon from '@mui/icons-material/Bolt';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import axiosInstance from '../../../../utils/axiosInstance';
import AgentSpaceTaskDetail from './AgentSpaceTaskDetail';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    textTertiary: '#94a3b8',
    accent: '#8b5cf6',
};

const AGENT_COLORS = {
    survey: '#3b82f6',
    crm: '#f59e0b',
    chat_history: '#6366f1',
    events: '#059669',
    gmail: '#dc2626',
    outlook: '#0078d4',
    'gmail-calendar': '#0891b2',
    gmail_cleanup: '#7c3aed',
    agent: '#8b5cf6',
};

const AGENT_ICONS = {
    survey: <PollIcon sx={{ fontSize: 18 }} />,
    crm: <PeopleIcon sx={{ fontSize: 18 }} />,
    chat_history: <ChatIcon sx={{ fontSize: 18 }} />,
    events: <EventIcon sx={{ fontSize: 18 }} />,
    gmail: <MailOutlineIcon sx={{ fontSize: 18 }} />,
    outlook: <EmailIcon sx={{ fontSize: 18 }} />,
    'gmail-calendar': <CalendarMonthIcon sx={{ fontSize: 18 }} />,
    gmail_cleanup: <CleaningServicesIcon sx={{ fontSize: 18 }} />,
    agent: <AutoAwesomeIcon sx={{ fontSize: 18 }} />,
};

const AGENT_LABELS = {
    survey: 'Survey',
    crm: 'CRM',
    chat_history: 'Chat History',
    events: 'Events',
    gmail: 'Gmail',
    outlook: 'Outlook',
    'gmail-calendar': 'Calendar',
    gmail_cleanup: 'Gmail Cleanup',
    agent: 'Agent',
};

function formatTimeAgo(isoString) {
    if (!isoString) return '';
    const now = new Date();
    const date = new Date(isoString);
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMs / 3600000);
    const diffDay = Math.floor(diffMs / 86400000);

    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHr < 24) return `${diffHr}h ago`;
    if (diffDay < 7) return `${diffDay}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
}

function formatDuration(ms) {
    if (!ms || ms < 1000) return '<1s';
    const seconds = Math.round(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.round(seconds / 60);
    return `${minutes}m`;
}

export default function AgentSpaceDashboard({ chatbotId, agentTypeFilter, onFilterChange }) {
    const [taskSummaries, setTaskSummaries] = useState([]);
    const [agentMetrics, setAgentMetrics] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [totalTasks, setTotalTasks] = useState(0);
    const [selectedActivity, setSelectedActivity] = useState(null);

    const fetchTaskSummaries = useCallback(async (pageNum = 1, append = false) => {
        if (!chatbotId || !agentTypeFilter) return;
        if (pageNum === 1) setLoading(true);
        else setLoadingMore(true);

        try {
            const response = await axiosInstance.get('/api/agent-space/dashboard/task-summaries', {
                params: { chatbot_id: chatbotId, agent_type: agentTypeFilter, page: pageNum, per_page: 5 },
            });
            if (response.data.success) {
                const data = response.data.data;
                setTaskSummaries(prev => append ? [...prev, ...data.summaries] : data.summaries);
                setHasMore(data.has_more);
                setPage(data.page);
                setTotalTasks(data.total);
                if (data.agent_metrics) setAgentMetrics(data.agent_metrics);
            }
        } catch (error) {
            console.error('Failed to fetch task summaries:', error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [chatbotId, agentTypeFilter]);

    useEffect(() => {
        setTaskSummaries([]);
        setPage(1);
        setHasMore(false);
        setAgentMetrics(null);
        fetchTaskSummaries(1, false);
    }, [fetchTaskSummaries]);

    const handleLoadMore = () => {
        fetchTaskSummaries(page + 1, true);
    };

    const agentColor = AGENT_COLORS[agentTypeFilter] || colors.accent;
    const agentLabel = AGENT_LABELS[agentTypeFilter] || agentTypeFilter || 'Agent';

    // Waiting for sidebar to set initial agent type
    if (!agentTypeFilter) {
        return (
            <Box sx={{
                height: '100%', display: 'flex', flexDirection: 'column',
                bgcolor: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(12px)',
                border: '1px solid rgba(0,0,0,0.06)', borderRadius: '16px', overflow: 'hidden',
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <CircularProgress size={28} sx={{ color: colors.accent }} />
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'rgba(255,255,255,0.6)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(0,0,0,0.06)',
            borderRadius: '16px',
            overflow: 'hidden',
        }}>
            {selectedActivity ? (
                <AgentSpaceTaskDetail
                    activity={selectedActivity}
                    chatbotId={chatbotId}
                    onBack={() => setSelectedActivity(null)}
                />
            ) : (
                <Box sx={{ flex: 1, overflow: 'auto', px: 2, pt: 2, pb: 1.5 }}>
                    {loading ? (
                        /* Skeleton loading */
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Metrics skeleton */}
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                                {[1, 2, 3].map(i => (
                                    <Skeleton key={i} variant="rounded" height={72} sx={{ flex: 1, borderRadius: '14px' }} />
                                ))}
                            </Box>
                            {/* Card skeletons */}
                            <Skeleton variant="rounded" width={120} height={16} sx={{ borderRadius: '6px' }} />
                            {[1, 2, 3, 4, 5].map(i => (
                                <Skeleton key={i} variant="rounded" height={130} sx={{ borderRadius: '14px' }} />
                            ))}
                        </Box>
                    ) : !agentMetrics || (agentMetrics.total_actions === 0 && taskSummaries.length === 0) ? (
                        /* Empty state */
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
                            <Box sx={{
                                width: 56, height: 56, borderRadius: '16px',
                                background: `linear-gradient(135deg, ${agentColor}15, ${agentColor}25)`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: agentColor,
                            }}>
                                {AGENT_ICONS[agentTypeFilter] || <AutoAwesomeIcon sx={{ fontSize: 28 }} />}
                            </Box>
                            <Typography sx={{ fontFamily, fontSize: '0.9rem', fontWeight: 600, color: colors.textSecondary }}>
                                No tasks yet for {agentLabel}
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.8rem', color: colors.textTertiary, textAlign: 'center', maxWidth: 300 }}>
                                Start a conversation with this agent to see task insights here
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

                            {/* ── Summary Metrics Row ── */}
                            {agentMetrics && (
                                <Box sx={{ display: 'flex', gap: 1.5 }}>
                                    {/* Total Conversations */}
                                    <Box sx={{
                                        flex: 1, p: 1.5, borderRadius: '14px',
                                        bgcolor: 'rgba(139,92,246,0.06)',
                                        border: '1px solid rgba(139,92,246,0.12)',
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                                            <ForumOutlinedIcon sx={{ fontSize: 15, color: colors.accent }} />
                                            <Typography sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 600, color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                Conversations
                                            </Typography>
                                        </Box>
                                        <Typography sx={{ fontFamily, fontSize: '1.4rem', fontWeight: 700, color: colors.textPrimary, lineHeight: 1 }}>
                                            {agentMetrics.total_conversations}
                                        </Typography>
                                    </Box>

                                    {/* Total Actions */}
                                    <Box sx={{
                                        flex: 1, p: 1.5, borderRadius: '14px',
                                        bgcolor: 'rgba(59,130,246,0.06)',
                                        border: '1px solid rgba(59,130,246,0.12)',
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                                            <BoltIcon sx={{ fontSize: 15, color: '#3b82f6' }} />
                                            <Typography sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 600, color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                Actions
                                            </Typography>
                                        </Box>
                                        <Typography sx={{ fontFamily, fontSize: '1.4rem', fontWeight: 700, color: colors.textPrimary, lineHeight: 1 }}>
                                            {agentMetrics.total_actions}
                                        </Typography>
                                    </Box>

                                    {/* Success Rate */}
                                    <Box sx={{
                                        flex: 1, p: 1.5, borderRadius: '14px',
                                        bgcolor: agentMetrics.success_rate >= 80 ? 'rgba(5,150,105,0.06)' : agentMetrics.success_rate >= 50 ? 'rgba(245,158,11,0.06)' : 'rgba(220,38,38,0.06)',
                                        border: `1px solid ${agentMetrics.success_rate >= 80 ? 'rgba(5,150,105,0.12)' : agentMetrics.success_rate >= 50 ? 'rgba(245,158,11,0.12)' : 'rgba(220,38,38,0.12)'}`,
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                                            <TrendingUpIcon sx={{ fontSize: 15, color: agentMetrics.success_rate >= 80 ? '#059669' : agentMetrics.success_rate >= 50 ? '#f59e0b' : '#dc2626' }} />
                                            <Typography sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 600, color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                Success
                                            </Typography>
                                        </Box>
                                        <Typography sx={{ fontFamily, fontSize: '1.4rem', fontWeight: 700, color: colors.textPrimary, lineHeight: 1 }}>
                                            {agentMetrics.success_rate}%
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            {/* ── Task Summaries ── */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography sx={{ fontFamily, fontSize: '0.72rem', fontWeight: 700, color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Task Insights ({totalTasks})
                                </Typography>

                                {taskSummaries.map((task) => {
                                    const taskColor = AGENT_COLORS[task.agent_type] || colors.accent;
                                    const statusColor = task.status === 'success' ? '#059669' : task.status === 'failed' ? '#dc2626' : '#f59e0b';
                                    const statusBg = task.status === 'success' ? 'rgba(5,150,105,0.1)' : task.status === 'failed' ? 'rgba(220,38,38,0.1)' : 'rgba(245,158,11,0.1)';
                                    const statusLabel = task.status === 'success' ? 'Success' : task.status === 'failed' ? 'Failed' : 'Partial';

                                    return (
                                        <Box
                                            key={task.session_code}
                                            onClick={() => setSelectedActivity({
                                                agent_type: task.agent_type,
                                                resource_name: task.title,
                                                status: task.status,
                                                timestamp: task.created_at,
                                                metadata: { session_code: task.session_code },
                                            })}
                                            sx={{
                                                p: 1.5, borderRadius: '14px',
                                                bgcolor: 'rgba(255,255,255,0.8)',
                                                border: '1px solid rgba(0,0,0,0.06)',
                                                borderLeft: `3px solid ${taskColor}`,
                                                cursor: 'pointer',
                                                transition: 'all 0.15s ease',
                                                '&:hover': {
                                                    bgcolor: 'rgba(255,255,255,1)',
                                                    border: `1px solid ${taskColor}20`,
                                                    borderLeft: `3px solid ${taskColor}`,
                                                    transform: 'translateY(-1px)',
                                                    boxShadow: `0 2px 8px ${taskColor}08`,
                                                },
                                            }}
                                        >
                                            {/* Card Header */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                                                <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 700, color: colors.textPrimary, flex: 1, mr: 1 }} noWrap>
                                                    {task.title || 'Untitled Task'}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                                                    <Box sx={{
                                                        display: 'flex', alignItems: 'center', gap: 0.4,
                                                        px: 0.75, py: 0.25, borderRadius: '6px', bgcolor: statusBg,
                                                    }}>
                                                        {task.status === 'success' && <CheckCircleIcon sx={{ fontSize: 10, color: statusColor }} />}
                                                        {task.status === 'failed' && <ErrorIcon sx={{ fontSize: 10, color: statusColor }} />}
                                                        <Typography sx={{ fontFamily, fontSize: '0.6rem', fontWeight: 600, color: statusColor }}>
                                                            {statusLabel}
                                                        </Typography>
                                                    </Box>
                                                    <Typography sx={{ fontFamily, fontSize: '0.6rem', color: colors.textTertiary }}>
                                                        {formatTimeAgo(task.created_at)}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {/* LLM Summary */}
                                            <Typography sx={{
                                                fontFamily, fontSize: '0.75rem', color: colors.textSecondary,
                                                lineHeight: 1.5, mb: task.key_actions?.length > 0 ? 0.75 : 0.5,
                                            }}>
                                                {task.summary}
                                            </Typography>

                                            {/* Key Actions */}
                                            {task.key_actions?.length > 0 && (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.75 }}>
                                                    {task.key_actions.map((action, i) => (
                                                        <Box key={i} sx={{
                                                            display: 'flex', alignItems: 'center', gap: 0.4,
                                                            px: 0.75, py: 0.25, borderRadius: '6px',
                                                            bgcolor: 'rgba(5,150,105,0.06)',
                                                            border: '1px solid rgba(5,150,105,0.1)',
                                                        }}>
                                                            <CheckCircleIcon sx={{ fontSize: 10, color: '#059669' }} />
                                                            <Typography sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 500, color: colors.textSecondary }}>
                                                                {action}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            )}

                                            {/* Footer: Step counts */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography sx={{ fontFamily, fontSize: '0.65rem', color: colors.textTertiary }}>
                                                    {task.steps_count} step{task.steps_count !== 1 ? 's' : ''}
                                                </Typography>
                                                {task.success_count > 0 && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                                        <CheckCircleIcon sx={{ fontSize: 10, color: '#059669' }} />
                                                        <Typography sx={{ fontFamily, fontSize: '0.65rem', color: '#059669' }}>
                                                            {task.success_count}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {task.failed_count > 0 && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                                        <ErrorIcon sx={{ fontSize: 10, color: '#dc2626' }} />
                                                        <Typography sx={{ fontFamily, fontSize: '0.65rem', color: '#dc2626' }}>
                                                            {task.failed_count}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    );
                                })}

                                {/* Load More */}
                                {hasMore && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
                                        <Button
                                            onClick={handleLoadMore}
                                            disabled={loadingMore}
                                            startIcon={loadingMore ? <CircularProgress size={14} color="inherit" /> : <KeyboardArrowDownIcon />}
                                            sx={{
                                                fontFamily, fontSize: '0.72rem', fontWeight: 600,
                                                textTransform: 'none', color: colors.accent, borderRadius: '10px', px: 3,
                                                '&:hover': { bgcolor: 'rgba(139,92,246,0.08)' },
                                            }}
                                        >
                                            {loadingMore ? 'Loading...' : 'Load More'}
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
}
